import type { ReactNode } from "react";
import { caregiverState, cn, inviteMinutesLeft, type PortalCaregiver } from "@dearbill/shared";

interface CaregiverRowProps {
  caregiver: PortalCaregiver;
  /** A `Date.now()` value; the pending countdown and the expired flip derive from it. */
  now: number;
}

/**
 * The caregiver's row: a dot, the name with the relationship, and the status
 * line. The confirmed row is the frame's green row (`moss` name, `fern`
 * status and dot on `success-bg`); both pending states use the neutral pair.
 * The status line is a polite live region, so the pending-to-expired flip and
 * a post-action change are announced without moving focus.
 */
export function CaregiverRow({ caregiver, now }: CaregiverRowProps): ReactNode {
  const state = caregiverState(caregiver, now);
  const confirmed = state === "confirmed";
  let status: string;
  if (confirmed) {
    status = "Active read/write permissions granted";
  } else if (state === "expired") {
    status = "Invitation expired";
  } else {
    const minutes = inviteMinutesLeft(caregiver.inviteExpiresAt, now);
    status = `Invitation sent to ${caregiver.email ?? ""}, expires in ${minutes} min`;
  }
  return (
    // 15px padding plus the 1px border is the frame's 16px inset, whose stroke
    // sits inside the padding, so the row measures 74px tall as designed.
    <div
      className={cn(
        "p-3.75 flex items-center gap-3 rounded-lg border",
        confirmed ? "bg-success-bg border-success-border" : "bg-ice border-haze",
      )}
    >
      <span
        aria-hidden="true"
        className={cn("size-2 shrink-0 rounded-full", confirmed ? "bg-fern" : "bg-deep-navy")}
      />
      <div className="text-status-row flex min-w-0 flex-1 flex-col gap-0.5">
        <p className={cn("font-semibold", confirmed ? "text-moss" : "text-midnight")}>
          {caregiver.firstName} {caregiver.lastName} ({caregiver.relationship})
        </p>
        <p aria-live="polite" className={confirmed ? "text-fern" : "text-slate-teal"}>
          {status}
        </p>
      </div>
    </div>
  );
}
